import React, { ReactNode, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { ModalComponent } from 'components';
import { USER_MENU_URL } from 'data/menu';
import {
  EnrollQuery,
  EventAdditionalQuery,
  EventFeedbackQuery,
  UserQuery,
} from 'queries';
import * as M from 'styles/mobile';
import { IEnroll } from 'types';
import { getQuestionResponse } from 'utils/question';
import { useAppSelector } from 'stores';
import * as S from './style';

interface Props {
  eventId: string;
  enroll: IEnroll;
  onClose: () => void;
}

type QuestionResponse = { id: number; title: string; response: string };

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section css={S.sheetSection}>
      <p css={S.label}>{title}</p>
      {children}
    </section>
  );
}

function Answers({
  items,
  emptyText,
}: {
  items: QuestionResponse[];
  emptyText: string;
}) {
  if (items.length === 0) return <p css={S.help}>{emptyText}</p>;

  return (
    <dl css={S.answers}>
      {items.map(({ id, title, response }) => (
        <div key={id}>
          <dt>{title}</dt>
          <dd>{response || '-'}</dd>
        </div>
      ))}
    </dl>
  );
}

function AttendeeSheet({ eventId, enroll, onClose }: Props) {
  const navigate = useNavigate();
  const { user } = enroll;
  // 회원 상세는 PlatformSuper 전용 (조직 어드민은 403)
  const canViewUser = useAppSelector(
    (s) => s.auth.user?.permissions.includes('user:detail') ?? false,
  );

  const { data: res } = EnrollQuery.useGetResponse(user.id, enroll.id, true);
  const { data: additional } = EventAdditionalQuery.useGet(eventId);
  const { data: feedback } = EventFeedbackQuery.useGet(eventId);
  const { data: userDetail } = UserQuery.useGet(String(user.id), canViewUser);

  const additionalItems: QuestionResponse[] = useMemo(
    () =>
      getQuestionResponse(additional?.questions || [], res?.additional || []),
    [additional, res],
  );
  const feedbackItems: QuestionResponse[] = useMemo(
    () =>
      res?.feedback
        ? getQuestionResponse(feedback?.questions || [], res.feedback)
        : [],
    [feedback, res],
  );

  return (
    <ModalComponent.Portal>
      <ModalComponent.Background>
        <ModalComponent.Modal
          title={
            <span css={S.sheetTitle}>
              {user.name}
              {canViewUser && (
                <button
                  type="button"
                  css={M.textButton}
                  onClick={() =>
                    navigate(`${USER_MENU_URL.회원목록}/${user.id}`)
                  }
                >
                  회원 상세
                </button>
              )}
            </span>
          }
          handleClose={onClose}
          footer={<></>}
        >
          <div css={S.sheet}>
            <Section title="추가정보">
              <Answers
                items={additionalItems}
                emptyText="추가정보가 없습니다."
              />
            </Section>
            <Section title="피드백">
              <Answers
                items={feedbackItems}
                emptyText="피드백 제출내용이 없습니다."
              />
            </Section>
            {canViewUser && (
              <Section title="메모">
                <p css={userDetail?.memo ? S.memo : S.help}>
                  {userDetail?.memo || '등록된 메모가 없습니다.'}
                </p>
              </Section>
            )}
          </div>
        </ModalComponent.Modal>
      </ModalComponent.Background>
    </ModalComponent.Portal>
  );
}

export default AttendeeSheet;
